import api from '@/lib/api'
import type { PaginatedResponse } from '@/types'

export interface AdminUser {
  id: string
  domain_id: string
  user_type_id: string
  parent_user_id: string | null
  name: string
  email: string | null
  phone: string | null
  timezone: string
  date_format: string
  is_active: boolean
  last_login_at: string | null
  created_at: string
  updated_at: string
  /** Joined relations */
  user_type?: { id: string; name: string }
  /** The user who created this account (joined from parent_user_id) */
  parent_user?: { id: string; name: string; email: string | null } | null
}

export interface UserListParams {
  search?: string
  user_type_id?: string
  is_active?: boolean
  page?: number
  per_page?: number
}

export interface CreateUserPayload {
  user_type_id: string
  name: string
  password: string
  email?: string
  phone?: string
  timezone?: string
  date_format?: string
  parent_user_id?: string
}

export type UpdateUserPayload = Partial<Omit<CreateUserPayload, 'password'>> & {
  is_active?: boolean
}

export const listUsers = (params?: UserListParams) =>
  api
    .get<PaginatedResponse<AdminUser>>('/users', { params })
    .then((r) => r.data)

export const getUser = (id: string) =>
  api
    .get<{ success: true; data: AdminUser }>(`/users/${id}`)
    .then((r) => r.data.data)

export const createUser = (payload: CreateUserPayload) =>
  api
    .post<{ success: true; data: AdminUser }>('/users', payload)
    .then((r) => r.data.data)

export const updateUser = (id: string, payload: UpdateUserPayload) =>
  api
    .put<{ success: true; data: AdminUser }>(`/users/${id}`, payload)
    .then((r) => r.data.data)

export const deleteUser = (id: string) =>
  api.delete(`/users/${id}`).then((r) => r.data)

export const resetUserPassword = (id: string, newPassword: string) =>
  api
    .post(`/users/${id}/reset-password`, { new_password: newPassword })
    .then((r) => r.data)

// ── Push tokens ────────────────────────────────────────────────────────────────

export interface PushToken {
  id: string
  token: string
  platform: 'android' | 'ios' | 'web'
  device_name: string | null
  is_active: boolean
  last_seen_at: string | null
  created_at: string
}

export const registerPushToken = (body: {
  token: string
  platform: PushToken['platform']
  device_name?: string
}) =>
  api
    .post<{ success: true; data: PushToken }>('/users/me/push-tokens', body)
    .then((r) => r.data.data)

export const revokePushToken = (id: string) =>
  api.delete(`/users/me/push-tokens/${id}`).then((r) => r.data)

export const listPushTokens = () =>
  api
    .get<{ success: true; data: PushToken[] }>('/users/me/push-tokens')
    .then((r) => r.data.data)

// ── User Contacts ──────────────────────────────────────────────────────────────

export interface UserContact {
  id: string
  user_id: string
  name: string
  relation: string | null
  phone: string | null
  email: string | null
  is_emergency: boolean
  created_at: string
}

export const listUserContacts = (userId: string) =>
  api
    .get<{ success: true; data: UserContact[] }>(`/users/${userId}/contacts`)
    .then((r) => r.data.data ?? [])

export const createUserContact = (userId: string, body: Partial<UserContact>) =>
  api
    .post<{ success: true; data: UserContact }>(`/users/${userId}/contacts`, body)
    .then((r) => r.data.data)

export const updateUserContact = (userId: string, contactId: string, body: Partial<UserContact>) =>
  api
    .put<{ success: true; data: UserContact }>(`/users/${userId}/contacts/${contactId}`, body)
    .then((r) => r.data.data)

export const deleteUserContact = (userId: string, contactId: string) =>
  api.delete(`/users/${userId}/contacts/${contactId}`).then((r) => r.data)
